import React, { useEffect, useState, useMemo, useCallback } from 'react' // Added useCallback
import { useNavigate, useLocation } from 'react-router-dom' // Added useLocation
import * as api from '../../../app/axios/api'
import UpdateRegistration from '../../../components/UpdateRegistion'
import { toast } from 'sonner'
import {
  Search,
  X,
  Download,
  Filter,
  ChevronDown,
  BookOpen,
  AlertCircle,
  Users,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ClipboardCheck,
  IndianRupee,
} from 'lucide-react'

const Students = () => {
  const [data, setData] = useState([])
  const navigate = useNavigate()
  const location = useLocation() // Initialize useLocation
  const [loading, setLoading] = useState(true)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [showCourseDropdown, setShowCourseDropdown] = useState(false) // Add this line
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const [showFeeStatusDropdown, setShowFeeStatusDropdown] = useState(false)

  // Initialize states from URL query parameters or default values
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )

  const [searchTerm, setSearchTerm] = useState(queryParams.get('search') || '')
  const [courseFilter, setCourseFilter] = useState(
    queryParams.get('course') || '',
  )
  const [statusFilter, setStatusFilter] = useState(
    queryParams.get('status') || 'running',
  )
  const [showNullCardId, setShowNullCardId] = useState(
    queryParams.get('missingCardId') === 'true',
  )
  const [feeStatusFilter, setFeeStatusFilter] = useState(
    queryParams.get('feeStatus') || '',
  )
  const [currentPage, setCurrentPage] = useState(
    parseInt(queryParams.get('page') || '1', 10),
  )
  const [itemsPerPage, setItemsPerPage] = useState(
    parseInt(queryParams.get('limit') || '25', 10),
  )

  const fetchStudents = useCallback(async () => {
    // Renamed from fetch to fetchStudents for clarity and made it useCallback
    try {
      setLoading(true)
      const res = await api.allStudent()
      setData(res.data || [])
    } catch (error) {
      console.error('Error fetching students:', error)
      toast.error('Failed to fetch students data')
      setData([])
    } finally {
      setLoading(false)
    }
  }, []) // No dependencies, as it fetches all students

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchStudents()
    }, 1000)

    return () => {
      clearTimeout(timer)
    }
  }, [fetchStudents]) // fetchStudents is now a dependency

  // Function to update URL with current filter states
  const updateUrlParams = useCallback(() => {
    const newParams = new URLSearchParams()
    if (searchTerm) newParams.set('search', searchTerm)
    if (courseFilter) newParams.set('course', courseFilter)
    if (statusFilter) newParams.set('status', statusFilter)
    if (showNullCardId) newParams.set('missingCardId', 'true')
    if (feeStatusFilter) newParams.set('feeStatus', feeStatusFilter)
    if (currentPage !== 1) newParams.set('page', currentPage.toString()) // Only add if not default
    if (itemsPerPage !== 25) newParams.set('limit', itemsPerPage.toString()) // Only add if not default

    navigate(`?${newParams.toString()}`, { replace: true })
  }, [
    searchTerm,
    courseFilter,
    statusFilter,
    showNullCardId,
    feeStatusFilter,
    currentPage,
    itemsPerPage,
    navigate,
  ])

  // Update URL whenever filter states or pagination changes
  useEffect(() => {
    updateUrlParams()
  }, [
    searchTerm,
    courseFilter,
    statusFilter,
    showNullCardId,
    feeStatusFilter,
    currentPage,
    itemsPerPage,
    updateUrlParams,
  ])

  const handleStudentClick = (studentId) => {
    // When navigating to details, the current URL will already have the filters.
    // The `details` page won't care about these, but when returning to `Students`,
    // `useLocation` will pick them up.
    navigate(`/students/${studentId}`)
  }

  // Edit Registration button click handler
  const handleEditClick = (e, student) => {
    e.stopPropagation()
    setSelectedStudent(student)
    setShowUpdateModal(true)
  }

  // Close modal handler
  const handleCloseModal = () => {
    setShowUpdateModal(false)
    setSelectedStudent(null)
  }

  const handleUpdateSubmit = async (formData) => {
    if (!selectedStudent || !selectedStudent.id) {
      toast.error('Student ID not found!')
      return
    }

    setUpdateLoading(true)
    try {
      const studentId = selectedStudent.id
      const res = await api.UpdateRegistrationFrom(studentId, formData)
      if (res.status === 200 || res.status === 201 || res.ok) {
        toast.success('Registration updated successfully!')
        setShowUpdateModal(false)
        setSelectedStudent(null)
        await fetchStudents() // Data refresh
      } else {
        toast.error(
          'Error updating registration: ' + (res.statusText || 'Unknown error'),
        )
      }
    } catch (error) {
      console.log(error)
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message)
      } else {
        toast.error('Failed to update registration')
      }
    } finally {
      setUpdateLoading(false)
    }
  }

  const uniqueCourses = useMemo(() => {
    const courses = [
      ...new Set(data.map((student) => student.course).filter(Boolean)),
    ]
    return courses.sort()
  }, [data])

  const uniqueStatuses = useMemo(() => {
    const statuses = [
      ...new Set(data.map((student) => student.status).filter(Boolean)),
    ]
    const desiredOrder = ['running', 'complete', 'left']
    return statuses.sort((a, b) => {
      const indexA = desiredOrder.indexOf(a.toLowerCase())
      const indexB = desiredOrder.indexOf(b.toLowerCase())
      if (indexA === -1 && indexB === -1) return a.localeCompare(b)
      if (indexA === -1) return 1
      if (indexB === -1) return -1
      return indexA - indexB
    })
  }, [data])

  const filteredData = useMemo(() => {
    let filtered = [...data]

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim()
      filtered = filtered.filter(
        (student) =>
          student.name?.toLowerCase().includes(searchLower) ||
          student.enrollment_number?.toLowerCase().includes(searchLower),
      )
    }

    if (courseFilter.trim()) {
      const courseLower = courseFilter.toLowerCase().trim()
      filtered = filtered.filter((student) =>
        student.course?.toLowerCase().includes(courseLower),
      )
    }

    if (statusFilter.trim()) {
      const statusLower = statusFilter.toLowerCase().trim()
      filtered = filtered.filter(
        (student) => student.status?.toLowerCase() === statusLower,
      )
    }

    if (showNullCardId) {
      filtered = filtered.filter(
        (student) =>
          !student.card_id ||
          student.card_id === null ||
          student.card_id === '',
      )
    }

    if (feeStatusFilter) {
      if (feeStatusFilter === 'paid') {
        filtered = filtered.filter((student) => (student.due_fee || 0) <= 0)
      } else if (feeStatusFilter === 'unpaid') {
        filtered = filtered.filter((student) => (student.due_fee || 0) > 0)
      }
    }

    return filtered
  }, [data, searchTerm, courseFilter, statusFilter, showNullCardId, feeStatusFilter])

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentData = filteredData.slice(startIndex, endIndex)

  // No longer reset to first page here, as current page is managed by URL.
  // The `updateUrlParams` useEffect handles syncing.

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const goToFirstPage = () => setCurrentPage(1)
  const goToLastPage = () => setCurrentPage(totalPages)
  const goToPreviousPage = () => setCurrentPage(Math.max(1, currentPage - 1))
  const goToNextPage = () =>
    setCurrentPage(Math.min(totalPages, currentPage + 1))

  const getPageNumbers = () => {
    const pageNumbers = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
      }
    } else {
      const startPage = Math.max(
        1,
        currentPage - Math.floor(maxVisiblePages / 2),
      )
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i)
      }

      if (startPage > 1) {
        if (startPage > 2) pageNumbers.unshift('...')
        pageNumbers.unshift(1)
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pageNumbers.push('...')
        pageNumbers.push(totalPages)
      }
    }

    return pageNumbers
  }

  const clearSearch = () => {
    setSearchTerm('')
  }

  const clearFilters = () => {
    setSearchTerm('')
    setCourseFilter('')
    setStatusFilter('')
    setShowNullCardId(false)
    setFeeStatusFilter('')
    setCurrentPage(1) // Reset page on clearing filters
  }

  const exportToCSV = () => {
    try {
      if (filteredData.length === 0) {
        toast.warning('No data to export!')
        return
      }

      const headers = [
        'Name',
        'Enrollment Number',
        'Course',
        'Father Name',
        'Phone',
        'Total Fee',
        'Discount',
        'Paid Amount',
        'Due Amount',
        'Card ID',
        'Registration Date',
        'Status',
      ]

      const csvData = filteredData.map((student) => [
        student.name || '',
        student.enrollment_number || '',
        student.course || '',
        student.father_name || '',
        student.phone || '',
        student.fee || 0,
        student.discount || 0,
        student.total_fee_submitted || 0,
        student.due_fee || 0,
        student.card_id || '',
        student.registration_date || '',
        student.status || '',
      ])

      const csvContent = [headers, ...csvData]
        .map((row) =>
          row
            .map((field) => `"${String(field).replace(/"/g, '""')}"`)
            .join(','),
        )
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute(
          'download',
          `students_data_${new Date().toISOString().split('T')[0]}.csv`,
        )
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        toast.success(
          `CSV exported successfully! ${filteredData.length} records exported.`,
        )
      }
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export CSV')
    }
  }

  const handleCourseSelect = (course) => {
    setCourseFilter(course)
    setShowCourseDropdown(false)
  }

  const handleStatusSelect = (status) => {
    setStatusFilter(status)
    setShowStatusDropdown(false)
  }

  const handleFeeStatusSelect = (status) => {
    setFeeStatusFilter(status)
    setShowFeeStatusDropdown(false)
    setCurrentPage(1)
  }

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='flex flex-col items-center'>
          <div className='w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin'></div>
          <p className='mt-4 text-gray-600 font-medium'>Loading data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-gray-50 p-4'>
      <div className='max-w-full mx-auto'>
        {/* Header */}
        <div className='mb-6 bg-white p-5 rounded-2xl shadow-sm border border-gray-200'>
          {/* Header Title */}
          <h1 className='text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight mb-3 flex items-center gap-2'>
            📊 All Students List
          </h1>

          {/* Stats Section */}
          <div className='flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3'>
            <span className='px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium text-center sm:text-left'>
              Total Students: {data.length}
            </span>

            <span className='px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium text-center sm:text-left'>
              Showing: {filteredData.length}
            </span>

            {filteredData.length > 0 && (
              <span className='px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm font-medium text-center sm:text-left'>
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
        </div>

        {/* Search Bar and Filters */}
        <div className='mb-4 bg-gradient-to-br from-white to-gray-50/80 rounded-xl shadow-md border border-gray-200/60 p-4'>
          {/* Main Search and Filters Section - Responsive Grid */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end mb-4'>
            {/* Search Input - spans full width on mobile, 2 columns on tablet, 1 on desktop */}
            <div className='sm:col-span-2 lg:col-span-1'>
              <label className='block text-xs font-semibold text-gray-500 mb-1'>
                Search
              </label>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <Search className='h-4 w-4 text-gray-400' />
                </div>
                <input
                  type='text'
                  placeholder='Search by student, enrollment...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className='block w-full pl-9 pr-9 py-2 border-0 bg-white rounded-lg shadow-sm ring-1 ring-gray-200 placeholder:text-gray-450 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-gray-50 text-sm transition-all duration-200 hover:shadow-md'
                />
                {searchTerm && (
                  <div className='absolute inset-y-0 right-0 pr-3 flex items-center'>
                    <button
                      onClick={clearSearch}
                      className='p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all duration-200'
                      title='Clear search'
                    >
                      <X className='h-4 w-4' />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Course Filter */}
            <div className='w-full'>
              <label className='text-xs font-semibold text-gray-500 mb-1 flex items-center'>
                <BookOpen className='h-3.5 w-3.5 mr-1 text-blue-500' />
                Course
              </label>
              <div className='relative'>
                <button
                  onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                  className='w-full px-3 py-2 text-left bg-white border-0 rounded-lg shadow-sm ring-1 ring-gray-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-gray-50 text-sm flex items-center justify-between transition-all duration-200 hover:shadow-md hover:ring-gray-300'
                >
                  <span className='truncate text-gray-700 font-medium'>
                    {courseFilter || 'All Courses'}
                  </span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${
                      showCourseDropdown ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {showCourseDropdown && (
                  <div className='absolute z-20 w-full mt-1 bg-white border-0 rounded-lg shadow-xl ring-1 ring-gray-200 max-h-60 overflow-y-auto'>
                    <div className='p-1'>
                      <button
                        onClick={() => handleCourseSelect('')}
                        className='w-full px-2 py-1.5 text-left hover:bg-gray-50 text-xs text-gray-700 rounded transition-colors duration-150 font-medium'
                      >
                        All Courses
                      </button>
                      {uniqueCourses.map((course) => (
                        <button
                          key={course}
                          onClick={() => handleCourseSelect(course)}
                          className={`w-full px-2 py-1.5 text-left hover:bg-gray-50 text-xs rounded transition-colors duration-150 ${
                            courseFilter === course
                              ? 'bg-blue-50 text-blue-700 font-semibold ring-1 ring-blue-200'
                              : 'text-gray-700'
                          }`}
                        >
                          {course}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div className='w-full'>
              <label className='text-xs font-semibold text-gray-500 mb-1 flex items-center'>
                <ClipboardCheck className='h-3.5 w-3.5 mr-1 text-green-500' />
                Status
              </label>
              <div className='relative'>
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className='w-full px-3 py-2 text-left bg-white border-0 rounded-lg shadow-sm ring-1 ring-gray-200 focus:ring-2 focus:ring-green-500 focus:ring-offset-1 focus:ring-offset-gray-50 text-sm flex items-center justify-between transition-all duration-200 hover:shadow-md hover:ring-gray-300'
                >
                  <span className='truncate text-gray-700 font-medium'>
                    {statusFilter || 'All Statuses'}
                  </span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${
                      showStatusDropdown ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {showStatusDropdown && (
                  <div className='absolute z-20 w-full mt-1 bg-white border-0 rounded-lg shadow-xl ring-1 ring-gray-200 max-h-60 overflow-y-auto'>
                    <div className='p-1'>
                      <button
                        onClick={() => handleStatusSelect('')}
                        className='w-full px-2 py-1.5 text-left hover:bg-gray-50 text-xs text-gray-700 rounded transition-colors duration-150 font-medium'
                      >
                        All Statuses
                      </button>
                      {uniqueStatuses.map((status) => (
                        <button
                          key={status}
                          onClick={() => handleStatusSelect(status)}
                          className={`w-full px-2 py-1.5 text-left hover:bg-gray-50 text-xs rounded transition-colors duration-150 ${
                            statusFilter === status
                              ? 'bg-green-50 text-green-700 font-semibold ring-1 ring-green-200'
                              : 'text-gray-700'
                          }`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fee Status Filter */}
            <div className='w-full'>
              <label className='text-xs font-semibold text-gray-500 mb-1 flex items-center'>
                <IndianRupee className='h-3.5 w-3.5 mr-1 text-indigo-500' />
                Fees
              </label>
              <div className='relative'>
                <button
                  onClick={() => setShowFeeStatusDropdown(!showFeeStatusDropdown)}
                  className='w-full px-3 py-2 text-left bg-white border-0 rounded-lg shadow-sm ring-1 ring-gray-200 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 focus:ring-offset-gray-50 text-sm flex items-center justify-between transition-all duration-200 hover:shadow-md hover:ring-gray-300'
                >
                  <span className='truncate text-gray-700 font-medium'>
                    {feeStatusFilter === 'paid'
                      ? 'Fees Paid'
                      : feeStatusFilter === 'unpaid'
                      ? 'Fees Unpaid'
                      : 'All Fees'}
                  </span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${
                      showFeeStatusDropdown ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {showFeeStatusDropdown && (
                  <div className='absolute z-20 w-full mt-1 bg-white border-0 rounded-lg shadow-xl ring-1 ring-gray-200 max-h-60 overflow-y-auto'>
                    <div className='p-1'>
                      <button
                        onClick={() => handleFeeStatusSelect('')}
                        className='w-full px-2 py-1.5 text-left hover:bg-gray-50 text-xs text-gray-700 rounded transition-colors duration-150 font-medium'
                      >
                        All Fees
                      </button>
                      <button
                        onClick={() => handleFeeStatusSelect('paid')}
                        className={`w-full px-2 py-1.5 text-left hover:bg-gray-50 text-xs rounded transition-colors duration-150 ${
                          feeStatusFilter === 'paid'
                            ? 'bg-indigo-50 text-indigo-700 font-semibold ring-1 ring-indigo-200'
                            : 'text-gray-700'
                        }`}
                      >
                        Fees Paid
                      </button>
                      <button
                        onClick={() => handleFeeStatusSelect('unpaid')}
                        className={`w-full px-2 py-1.5 text-left hover:bg-gray-50 text-xs rounded transition-colors duration-150 ${
                          feeStatusFilter === 'unpaid'
                            ? 'bg-indigo-50 text-indigo-700 font-semibold ring-1 ring-indigo-200'
                            : 'text-gray-700'
                        }`}
                      >
                        Fees Unpaid
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Secondary Controls - Responsive Flex Wrapper with Separator */}
          <div className='flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end justify-between gap-3 pt-3 border-t border-gray-200/80'>
            <div className='flex flex-wrap items-center gap-3 flex-1'>
              {/* Items per page selector */}
              <div className='flex-1 sm:flex-initial min-w-[110px]'>
                <label className='block text-xs font-semibold text-gray-500 mb-1'>
                  Show Limit
                </label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className='w-full px-3 py-2 text-left bg-white border-0 rounded-lg shadow-sm ring-1 ring-gray-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-gray-50 text-sm transition-all duration-200 hover:shadow-md hover:ring-gray-300'
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {/* Special Filter Checkbox */}
              <div className='flex-1 sm:flex-initial min-w-[150px]'>
                <label className='block text-xs font-semibold text-gray-500 mb-1'>
                  Special Filters
                </label>
                <div className='bg-white rounded-lg shadow-sm ring-1 ring-gray-200 px-3 py-2 h-[38px] flex items-center'>
                  <div className='flex items-center whitespace-nowrap'>
                    <input
                      type='checkbox'
                      id='nullCardId'
                      checked={showNullCardId}
                      onChange={(e) => setShowNullCardId(e.target.checked)}
                      className='h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 border-gray-300 rounded transition-colors duration-150'
                    />
                    <label
                      htmlFor='nullCardId'
                      className='ml-2 text-xs font-medium text-gray-700 cursor-pointer'
                    >
                      Missing Card ID
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='flex items-center justify-end gap-2'>
              {(searchTerm ||
                courseFilter ||
                statusFilter ||
                showNullCardId ||
                feeStatusFilter) && (
                <button
                  onClick={clearFilters}
                  className='px-3 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-200 flex items-center justify-center space-x-1.5 hover:shadow-sm whitespace-nowrap'
                >
                  <Filter className='h-3.5 w-3.5' />
                  <span className='hidden sm:inline'>Reset</span>
                </button>
              )}

              <button
                onClick={exportToCSV}
                disabled={filteredData.length === 0}
                className='px-3 py-2 text-sm font-medium bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-400 text-white rounded-lg transition-all duration-200 flex items-center justify-center space-x-1.5 shadow-sm hover:shadow-md disabled:shadow-none disabled:cursor-not-allowed whitespace-nowrap'
              >
                <Download className='h-3.5 w-3.5' />
                <span className='hidden sm:inline'>Export</span>
                <span className='sm:hidden'>CSV</span>
              </button>
            </div>
          </div>

          {/* Active Filters Display */}
          {(searchTerm || courseFilter || statusFilter || showNullCardId || feeStatusFilter) && (
            <div className='p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-gray-200/60'>
              <div className='flex flex-wrap items-center gap-3 mb-3'>
                <span className='text-sm font-semibold text-gray-600 flex items-center'>
                  <Filter className='h-4 w-4 mr-1' />
                  Active Filters:
                </span>

                {searchTerm && (
                  <span className='inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ring-1 ring-blue-200 transition-all duration-150 hover:bg-blue-200'>
                    <Search className='h-3 w-3 mr-1.5' />"{searchTerm}"
                    <button
                      onClick={() => setSearchTerm('')}
                      className='ml-2 text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full p-0.5 transition-all duration-150'
                    >
                      <X className='h-3 w-3' />
                    </button>
                  </span>
                )}

                {courseFilter && (
                  <span className='inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 ring-1 ring-purple-200 transition-all duration-150 hover:bg-purple-200'>
                    <BookOpen className='h-3 w-3 mr-1.5' />
                    {courseFilter}
                    <button
                      onClick={() => setCourseFilter('')}
                      className='ml-2 text-purple-600 hover:text-purple-800 hover:bg-purple-200 rounded-full p-0.5 transition-all duration-150'
                    >
                      <X className='h-3 w-3' />
                    </button>
                  </span>
                )}

                {statusFilter && ( // New status filter display
                  <span className='inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ring-1 ring-green-200 transition-all duration-150 hover:bg-green-200'>
                    <ClipboardCheck className='h-3 w-3 mr-1.5' />
                    {statusFilter.charAt(0).toUpperCase() +
                      statusFilter.slice(1)}
                    <button
                      onClick={() => setStatusFilter('')}
                      className='ml-2 text-green-600 hover:text-green-800 hover:bg-green-200 rounded-full p-0.5 transition-all duration-150'
                    >
                      <X className='h-3 w-3' />
                    </button>
                  </span>
                )}

                {showNullCardId && (
                  <span className='inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 ring-1 ring-orange-200 transition-all duration-150 hover:bg-orange-200'>
                    <AlertCircle className='h-3 w-3 mr-1.5' />
                    Missing Card ID
                    <button
                      onClick={() => setShowNullCardId(false)}
                      className='ml-2 text-orange-600 hover:text-orange-800 hover:bg-orange-200 rounded-full p-0.5 transition-all duration-150'
                    >
                      <X className='h-3 w-3' />
                    </button>
                  </span>
                )}

                {feeStatusFilter && (
                  <span className='inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200 transition-all duration-150 hover:bg-indigo-200'>
                    <IndianRupee className='h-3 w-3 mr-1.5' />
                    {feeStatusFilter === 'paid' ? 'Paid' : 'Unpaid'}
                    <button
                      onClick={() => setFeeStatusFilter('')}
                      className='ml-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-200 rounded-full p-0.5 transition-all duration-150'
                    >
                      <X className='h-3 w-3' />
                    </button>
                  </span>
                )}

                {/* Clear all filters button */}
                <button
                  onClick={clearFilters}
                  className='text-sm text-gray-500 hover:text-gray-700 font-medium underline decoration-dotted underline-offset-2 transition-colors duration-150'
                >
                  Clear All
                </button>
              </div>

              {/* Enhanced Results Count */}
              <div className='flex items-center justify-between text-sm border-t border-gray-200/60 pt-3'>
                <div className='flex items-center space-x-2 text-gray-600'>
                  <Users className='h-4 w-4 text-blue-500' />
                  <span>
                    <span className='font-bold text-gray-900'>
                      {filteredData.length}
                    </span>{' '}
                    student{filteredData.length !== 1 ? 's' : ''} found
                  </span>
                </div>

                {/* Additional stats */}
                <div className='hidden md:flex items-center space-x-4 text-xs text-gray-500'>
                  {searchTerm && <span>Search results</span>}
                  {courseFilter && <span>From {courseFilter}</span>}
                  {statusFilter && <span>Status: {statusFilter}</span>}{' '}
                  {/* Add status here */}
                  {showNullCardId && <span>Missing Card ID</span>}
                  {feeStatusFilter && <span>Fees Status: {feeStatusFilter === 'paid' ? 'Paid' : 'Unpaid'}</span>}
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredData?.length === 0 ? (
          <div className='text-center py-8'>
            <div className='bg-white rounded-lg shadow p-6'>
              {searchTerm || courseFilter || statusFilter || showNullCardId || feeStatusFilter ? (
                <>
                  <div className='text-gray-400 text-4xl mb-2'>🔍</div>
                  <h3 className='text-lg font-semibold text-gray-900 mb-1'>
                    No Students Found
                  </h3>
                  <p className='text-gray-600 text-sm mb-3'>
                    No students match your current filters
                  </p>
                  <button
                    onClick={clearFilters}
                    className='text-blue-600 hover:text-blue-800 text-sm font-medium'
                  >
                    Clear all filters to see all students
                  </button>
                </>
              ) : (
                <>
                  <div className='text-gray-400 text-4xl mb-2'>📚</div>
                  <h3 className='text-lg font-semibold text-gray-900 mb-1'>
                    No Students Found
                  </h3>
                  <p className='text-gray-600 text-sm'>
                    There are no students registered yet.
                  </p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className='w-full max-w-full'>
            {/* Desktop/Tablet View */}
            <div className='hidden md:block bg-white rounded-lg shadow overflow-hidden'>
              {/* Table Header */}
              <div className='bg-gray-100 border-b border-gray-200'>
                <div className='grid grid-cols-12 gap-2 p-3 text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                  <div className='col-span-1 text-center'>S.No.</div>
                  <div className='col-span-2'>Student</div>
                  <div className='col-span-1'>Enrollment</div>
                  <div className='col-span-1'>Academic</div>
                  <div className='col-span-1'>Add Date</div>
                  <div className='col-span-1'>Guardian</div>
                  <div className='col-span-1 text-right'>Total Fee</div>
                  <div className='col-span-1 text-right'>Discount</div>
                  <div className='col-span-1 text-right'>Paid</div>
                  <div className='col-span-1 text-right'>Due</div>
                  <div className='col-span-1 text-center'>Actions</div>
                </div>
              </div>

              {/* Table Body */}
              <div className='divide-y divide-gray-200'>
                {currentData?.map((student, index) => (
                  <div
                    key={student.id || index}
                    onClick={() => handleStudentClick(student.id)}
                    className={`grid grid-cols-12 gap-2 p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                      student.due_fee > 0
                        ? 'bg-red-50 border-l-2 border-red-500'
                        : 'bg-green-50 border-l-2 border-green-500'
                    }`}
                  >
                    {/* S.No. */}
                    <div className='col-span-1 flex items-center justify-center text-sm text-gray-600 font-medium'>
                      {startIndex + index + 1}
                    </div>
                    {/* Student Info */}
                    <div className='col-span-2 flex items-center space-x-2'>
                      <div className='w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0'>
                        <span className='text-white text-xs font-bold'>
                          {student.name?.charAt(0)?.toUpperCase() || 'S'}
                        </span>
                      </div>
                      <div className='min-w-0'>
                        <p className='text-sm font-semibold text-gray-900 truncate'>
                          {student.name || 'Unknown Student'}
                        </p>
                        <div className='flex items-center space-x-1'>
                          {student.due_fee > 0 ? (
                            <span className='inline-block bg-red-100 text-red-800 text-xs font-bold px-1 py-0.5 rounded'>
                              DUE
                            </span>
                          ) : (
                            <span className='inline-block bg-green-100 text-green-800 text-xs font-semibold px-1 py-0.5 rounded'>
                              PAID
                            </span>
                          )}
                          {(!student.card_id ||
                            student.card_id === null ||
                            student.card_id === '') && (
                            <span className='inline-block bg-orange-100 text-orange-800 text-xs px-1 py-0.5 rounded'>
                              NO CARD
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Enrollment */}
                    <div className='col-span-1 flex items-center'>
                      <span className='text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded truncate'>
                        {student.enrollment_number || 'N/A'}
                      </span>
                    </div>

                    {/* Academic Info */}
                    <div className='col-span-1 flex items-center'>
                      <div className='min-w-0'>
                        <p className='text-sm font-medium text-gray-900 truncate'>
                          {student.course || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Add date */}
                    <div className='col-span-1 flex items-center'>
                      <div className='min-w-0'>
                        <p className='text-sm font-medium text-gray-900 truncate'>
                          {new Date(student.created_at).toLocaleString(
                            'en-IN',
                            {
                              dateStyle: 'medium',
                            },
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Guardian */}
                    <div className='col-span-1 flex items-center'>
                      <p className='text-sm text-gray-900 truncate'>
                        {student.father_name || 'N/A'}
                      </p>
                    </div>

                    {/* Total Fee */}
                    <div className='col-span-1 flex items-center justify-end'>
                      <span className='text-sm font-medium text-gray-900'>
                        ₹{student.fee || 0}
                      </span>
                    </div>

                    {/* Discount */}
                    <div className='col-span-1 flex items-center justify-end'>
                      <span className='text-sm font-medium text-green-600'>
                        -₹{student.discount || 0}
                      </span>
                    </div>

                    {/* Paid Amount */}
                    <div className='col-span-1 flex items-center justify-end'>
                      <span className='text-sm font-medium text-blue-600'>
                        ₹{student.total_fee_submitted || 0}
                      </span>
                    </div>

                    {/* Due Amount */}
                    <div className='col-span-1 flex items-center justify-end'>
                      <span
                        className={`text-sm font-bold ${
                          (student.due_fee || 0) > 0
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}
                      >
                        ₹{student.due_fee || 0}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className='col-span-1 flex items-center justify-center space-x-2'>
                      <button
                        onClick={(e) => handleEditClick(e, student)}
                        className='bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded transition-colors'
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleStudentClick(student.id)
                        }}
                        className='bg-gray-500 hover:bg-gray-600 text-white text-xs px-2 py-1 rounded transition-colors'
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile View - Card Layout */}
            <div className='md:hidden space-y-4'>
              {currentData?.map((student, index) => (
                <div
                  key={student.id || index}
                  onClick={() => handleStudentClick(student.id)}
                  className={`bg-white rounded-lg shadow-sm border cursor-pointer transition-all duration-200 hover:shadow-md ${
                    student.due_fee > 0
                      ? 'border-l-4 border-l-red-500 bg-red-50'
                      : 'border-l-4 border-l-green-500 bg-green-50'
                  }`}
                >
                  {/* Student Header */}
                  <div className='p-4 border-b border-gray-200'>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center space-x-3 min-w-0'>
                        {/* Serial Number */}
                        <div className='flex-shrink-0 font-bold text-gray-500 text-sm'>
                          {startIndex + index + 1}.
                        </div>

                        <div className='w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0'>
                          <span className='text-white text-sm font-bold'>
                            {student.name?.charAt(0)?.toUpperCase() || 'S'}
                          </span>
                        </div>
                        <div>
                          <h3 className='text-sm font-semibold text-gray-900'>
                            {student.name || 'Unknown Student'}
                          </h3>
                          <p className='text-xs text-gray-600'>
                            {student.enrollment_number || 'N/A'}
                          </p>
                          <p className='text-xs  text-gray-500 truncate'>
                            {new Date(student.created_at).toLocaleString(
                              'en-IN',
                              {
                                dateStyle: 'medium',
                              },
                            )}
                          </p>
                        </div>
                      </div>
                      <div className='flex flex-col items-end space-y-1'>
                        {student.due_fee > 0 ? (
                          <span className='inline-block bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded'>
                            DUE
                          </span>
                        ) : (
                          <span className='inline-block bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded'>
                            PAID
                          </span>
                        )}
                        {(!student.card_id ||
                          student.card_id === null ||
                          student.card_id === '') && (
                          <span className='inline-block bg-orange-100 text-orange-800 text-xs font-bold px-2 py-1 rounded'>
                            NO CARD
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Student Details */}
                  <div className='p-4 space-y-3'>
                    {/* Course and Father */}
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <p className='text-xs text-gray-500 font-medium'>
                          Course
                        </p>
                        <p className='text-sm text-gray-900'>
                          {student.course || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className='text-xs text-gray-500 font-medium'>
                          Guardian
                        </p>
                        <p className='text-sm text-gray-900'>
                          {student.father_name || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Fee Details */}
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <p className='text-xs text-gray-500 font-medium'>
                          Total Fee
                        </p>
                        <p className='text-sm font-semibold text-gray-900'>
                          ₹{student.fee || 0}
                        </p>
                      </div>
                      <div>
                        <p className='text-xs text-gray-500 font-medium'>
                          Discount
                        </p>
                        <p className='text-sm font-semibold text-green-600'>
                          -₹{student.discount || 0}
                        </p>
                      </div>
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <p className='text-xs text-gray-500 font-medium'>
                          Paid
                        </p>
                        <p className='text-sm font-semibold text-blue-600'>
                          ₹{student.total_fee_submitted || 0}
                        </p>
                      </div>
                      <div>
                        <p className='text-xs text-gray-500 font-medium'>Due</p>
                        <p
                          className={`text-sm font-bold ${
                            (student.due_fee || 0) > 0
                              ? 'text-red-600'
                              : 'text-green-600'
                          }`}
                        >
                          ₹{student.due_fee || 0}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className='flex space-x-2 pt-2'>
                      <button
                        onClick={(e) => handleEditClick(e, student)}
                        className='flex-1 bg-blue-500 hover:bg-blue-600 text-white text-sm px-3 py-2 rounded transition-colors'
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleStudentClick(student.id)
                        }}
                        className='flex-1 bg-gray-500 hover:bg-gray-600 text-white text-sm px-3 py-2 rounded transition-colors'
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className='mt-6 bg-white rounded-xl shadow-lg border border-gray-200/60 p-4'>
                <div className='flex flex-col lg:flex-row items-center justify-between gap-4'>
                  {/* Pagination Info */}
                  <div className='text-sm text-gray-600'>
                    Showing{' '}
                    <span className='font-semibold text-gray-900'>
                      {startIndex + 1}
                    </span>{' '}
                    to{' '}
                    <span className='font-semibold text-gray-900'>
                      {Math.min(endIndex, filteredData.length)}
                    </span>{' '}
                    of{' '}
                    <span className='font-semibold text-gray-900'>
                      {filteredData.length}
                    </span>{' '}
                    results
                  </div>

                  {/* Pagination Controls */}
                  <div className='flex items-center space-x-1'>
                    {/* First Page */}
                    <button
                      onClick={goToFirstPage}
                      disabled={currentPage === 1}
                      className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                      title='First page'
                    >
                      <ChevronsLeft className='h-4 w-4' />
                    </button>

                    {/* Previous Page */}
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                      title='Previous page'
                    >
                      <ChevronLeft className='h-4 w-4' />
                    </button>

                    {/* Page Numbers */}
                    <div className='flex items-center space-x-1 mx-2'>
                      {getPageNumbers().map((pageNumber, index) => (
                        <React.Fragment key={index}>
                          {pageNumber === '...' ? (
                            <span className='px-2 py-1 text-gray-400'>...</span>
                          ) : (
                            <button
                              onClick={() => goToPage(pageNumber)}
                              className={`min-w-[36px] h-9 text-sm font-medium rounded-lg transition-all duration-200 ${
                                currentPage === pageNumber
                                  ? 'bg-blue-600 text-white shadow-sm'
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    {/* Next Page */}
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                      title='Next page'
                    >
                      <ChevronRight className='h-4 w-4' />
                    </button>

                    {/* Last Page */}
                    <button
                      onClick={goToLastPage}
                      disabled={currentPage === totalPages}
                      className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                      title='Last page'
                    >
                      <ChevronsRight className='h-4 w-4' />
                    </button>
                  </div>

                  {/* Jump to Page */}
                  <div className='flex items-center space-x-2'>
                    <span className='text-sm text-gray-600'>Go to:</span>
                    <input
                      type='number'
                      min='1'
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        const page = parseInt(e.target.value)
                        if (page >= 1 && page <= totalPages) {
                          goToPage(page)
                        }
                      }}
                      className='w-16 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    />
                    <span className='text-sm text-gray-600'>
                      of {totalPages}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {(showCourseDropdown || showStatusDropdown || showFeeStatusDropdown) && (
        <div
          className='fixed inset-0 z-5'
          onClick={() => {
            setShowCourseDropdown(false)
            setShowStatusDropdown(false)
            setShowFeeStatusDropdown(false)
          }}
        ></div>
      )}

      {/* Update Registration Modal */}
      {showUpdateModal && selectedStudent && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center p-4 bg-blue-500 border-b border-gray-200'>
              <h2 className='text-2xl font-bold text-white'>
                Update Registration - {selectedStudent.name}
              </h2>
              <button
                onClick={handleCloseModal}
                className='text-white hover:text-red-500 text-2xl font-bold'
                disabled={updateLoading}
              >
                ×
              </button>
            </div>

            <div className='p-6'>
              <UpdateRegistration
                initialData={selectedStudent}
                onSubmit={handleUpdateSubmit}
                loading={updateLoading}
                onCancel={handleCloseModal}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Students
